import groupBy from "lodash/groupBy";
import isEqual from "lodash/isEqual";
import transform from "lodash/transform";
import React from "react";
import { Detail } from "react-calendar";
import {
  CalendarDate,
  DatePickerType,
  DatePickerUniversal,
  DateValidity,
  ErrorMessageLocation,
  FilterControl,
  FilterDrawer,
  ICheckboxGroupValue,
  IFilterTagProps,
} from "component-library";

import { IClientAlertsPageTexts } from "pages/JobsPage/ClientAlertsPage/ClientAlertsPage";

export enum FilterTypes {
  dateValue = "DateValue",
}

export enum FilterControlTypes {
  datePicker = "datePicker",
  dropdownCheckbox = "dropdownCheckbox",
}

interface IFilterConfigItem {
  translations: string;
  defaultValue: any;
  filterType: FilterControlTypes;
}

interface IFilterDataModel {
  [FilterTypes.dateValue]: CalendarDate | undefined;
}

interface IFilterDrawerDataModel extends IFilterDataModel {}

export type IFilterValues = {
  [K in keyof IFilterDataModel]: IFilterDataModel[K];
};

type IFilterDrawerValues = {
  [K in keyof IFilterDrawerDataModel]: IFilterDrawerDataModel[K];
};

type IFilterConfig = {
  [K in keyof IFilterDrawerDataModel]: IFilterConfigItem;
};

export interface IClientAlertsFilterTagProps extends IFilterTagProps {
  filterType: FilterTypes;
}

interface IClientAlertsFilterControlState {
  values: IFilterValues;
  tags: IClientAlertsFilterTagProps[];
  isViewTagsPopupShown: boolean;
  isFilterDrawerShown: boolean;
  filterDrawerValues: IFilterDrawerValues;
}

interface IClientAlertsFilterControlProps {
  locale: string;
  texts: IClientAlertsPageTexts;
  onValueChange: (values: IFilterValues) => void;
  paginationContent?: React.ReactNode;
}

export class ClientAlertsFilterControl extends React.Component<
  IClientAlertsFilterControlProps,
  IClientAlertsFilterControlState
> {
  private setupFilterConfig = (): IFilterConfig => {
    return {
      [FilterTypes.dateValue]: {
        translations: this.props.texts.filterDateFilter,
        defaultValue: undefined,
        filterType: FilterControlTypes.datePicker,
      },
    };
  };

  private filterConfig: IFilterConfig = this.setupFilterConfig();

  public readonly state: Readonly<IClientAlertsFilterControlState> = {
    isViewTagsPopupShown: false,
    tags: [],
    values: {
      [FilterTypes.dateValue]:
        this.filterConfig[FilterTypes.dateValue].defaultValue,
    },
    filterDrawerValues: {
      [FilterTypes.dateValue]:
        this.filterConfig[FilterTypes.dateValue].defaultValue,
    },
    isFilterDrawerShown: false,
  };

  public componentDidUpdate(prevProps: IClientAlertsFilterControlProps) {
    if (
      !isEqual(this.props.texts, prevProps.texts) ||
      this.props.locale !== prevProps.locale
    ) {
      this.filterConfig = this.setupFilterConfig();
      this.updateAllTags(this.state.values);
    }
  }

  public render() {
    const { locale, texts } = this.props;
    const { tags, values, isFilterDrawerShown } = this.state;

    return (
      <div className="g-bg-secondary c-client-alert-filter-control">
        <FilterControl
          onViewAllFilters={this.openFilterDrawer}
          onClearAllFilters={this.onClearAllFilters}
          onApplyTags={this.handleApplyTags}
          locale={locale}
          tags={tags}
          tagsEndContent={this.props.paginationContent}
          items={[
            <DatePickerUniversal
              locale={locale}
              key={DatePickerType.Range}
              type={DatePickerType.Range}
              placeholder={texts.filterDateFilter}
              minDate={new Date(1900, 0, 1)}
              errorMessageLocation={ErrorMessageLocation.Calendar}
              valueChanged={this.updateFilterDateValue(FilterTypes.dateValue)}
              value={values[FilterTypes.dateValue]}
            />,
          ]}
        />
        {isFilterDrawerShown && (
          <FilterDrawer
            filters={this.prepareFilters()}
            locale={locale}
            onClose={this.closeFilterDrawer}
            onClearAllFilters={this.onClearAllFiltersInFilterDrawer}
            onApplyFilters={this.applyFilters}
          />
        )}
      </div>
    );
  }

  private getTagIdByFilterData = (
    filterId: FilterTypes,
    optionId: string = ""
  ) => `${filterId}_${optionId}`;

  private getTagName = (translation: string, value: string): string => {
    return `${translation}: ${value}`;
  };

  private handleApplyTags = (tags: IFilterTagProps[]) => {
    const removedTags = this.state.tags.filter((tag) =>
      tags.every((t) => tag.id !== t.id)
    );
    this.removeFilterTags(removedTags);
  };

  private removeFilterTags = (tags: IClientAlertsFilterTagProps[]) => {
    const updatedValues = transform(
      groupBy(tags, (tag) => tag.filterType),
      (model, tagsByType, filterType) =>
        (model[filterType] = this.filterConfig[filterType].defaultValue),
      { ...this.state.values }
    );
    this.setState(
      {
        values: updatedValues,
      },
      () => this.props.onValueChange(this.state.values)
    );
  };

  private onClearAllFilters = () => {
    const values = {} as IFilterValues;

    Object.keys(this.state.values).forEach((filterId: FilterTypes) => {
      values[filterId] = this.filterConfig[filterId].defaultValue;
    });

    this.setState(
      {
        values,
        tags: [],
      },
      () => this.props.onValueChange(this.state.values)
    );
  };

  private onClearAllFiltersInFilterDrawer = () => {
    const filterDrawerValues = {} as IFilterDrawerValues;

    Object.keys(this.state.filterDrawerValues).forEach(
      (filterId: FilterTypes) => {
        filterDrawerValues[filterId] = this.filterConfig[filterId].defaultValue;
      }
    );

    this.setState({
      filterDrawerValues,
    });
  };

  private updateFilterDateValue =
    (id: FilterTypes) =>
    (date: Date[], detailsLevel: Detail, validity: DateValidity) => {
      this.onDatePickerValueChanged(id, date, detailsLevel, validity);
    };

  private onDatePickerValueChanged = (
    id: FilterTypes,
    date: Date[],
    detailsLevel: Detail,
    validity: DateValidity
  ) => {
    if (
      validity === DateValidity.Valid &&
      (date === undefined || date.length > 1)
    ) {
      const tags = this.getUpdatedTags(id, date);
      this.setState(
        { values: { ...this.state.values, [id]: date }, tags },
        () => this.props.onValueChange(this.state.values)
      );
    }
  };

  private updateFilterDrawerDateValue =
    (id: FilterTypes) =>
    (date: Date[], detailsLevel: Detail, validity: DateValidity) => {
      this.onDatePickerDrawerValueChanged(id, date, detailsLevel, validity);
    };

  private onDatePickerDrawerValueChanged = (
    id: FilterTypes,
    date: Date[],
    detailsLevel: Detail,
    validity: DateValidity
  ) => {
    if (
      validity === DateValidity.Valid &&
      (date === undefined || date.length > 1)
    ) {
      this.setState({
        filterDrawerValues: { ...this.state.filterDrawerValues, [id]: date },
      });
    }
  };

  private getUpdatedTags = (
    filterId: FilterTypes,
    value: CalendarDate | ICheckboxGroupValue,
    initialTags = this.state.tags
  ) => {
    let newArr = initialTags.slice();
    const { translations, filterType } = this.filterConfig[filterId];

    if (filterType === FilterControlTypes.datePicker) {
      const tagId = this.getTagIdByFilterData(filterId, filterId);
      newArr = newArr.filter((tag) => tag.id !== tagId);

      if (value !== undefined) {
        if (!newArr.find((tag) => tag.id === tagId)) {
          const stringValue = DatePickerUniversal.getStringValue(
            value as CalendarDate
          );
          const tag = {
            text: this.getTagName(translations, stringValue),
            id: tagId,
            onClick: () => this.removeSelectedFilter(filterId, tag.id),
            filterType: filterId,
          };
          newArr.push(tag);
        }
      }
    }

    if (filterType === FilterControlTypes.dropdownCheckbox) {
      // TODO: integrate filter for dropdownCheckbox
    }

    return newArr;
  };

  private updateAllTags = (
    values: IFilterDataModel | IFilterDrawerDataModel
  ) => {
    let newTags: IClientAlertsFilterTagProps[] = [];

    Object.keys(FilterTypes).forEach((key) => {
      const filter = FilterTypes[key];
      const newValues = values[filter];

      newTags = this.getUpdatedTags(filter, newValues, newTags);
    });

    this.setState({ tags: newTags });
  };

  private openFilterDrawer = () => {
    this.setState({
      isFilterDrawerShown: true,
      filterDrawerValues: { ...this.state.values },
    });
  };

  private closeFilterDrawer = () => {
    this.setState({ isFilterDrawerShown: false });
  };

  private applyFilters = () => {
    this.updateAllTags(this.state.filterDrawerValues);
    this.setState(
      {
        isFilterDrawerShown: false,
        values: { ...this.state.filterDrawerValues },
      },
      () => this.props.onValueChange(this.state.values)
    );
  };

  private prepareFilters = () => {
    const { texts, locale } = this.props;

    return [
      {
        title: texts.filterDateFilter,
        id: "DatePicker",
        selected:
          this.state.filterDrawerValues[FilterTypes.dateValue] !== undefined,
        renderFilterContent: () => (
          <DatePickerUniversal
            locale={locale}
            key={DatePickerType.Range}
            type={DatePickerType.Range}
            placeholder={texts.filterDateFilter}
            minDate={new Date(1900, 0, 1)}
            errorMessageLocation={ErrorMessageLocation.Calendar}
            valueChanged={this.updateFilterDrawerDateValue(
              FilterTypes.dateValue
            )}
            value={this.state.filterDrawerValues[FilterTypes.dateValue]}
          />
        ),
      },
    ];
  };

  private removeSelectedFilter = (filterId: FilterTypes, tagId: string) => {
    const newArr = this.state.tags.filter((tag) => tag.id !== tagId);
    this.setState(
      {
        values: {
          ...this.state.values,
          [filterId]: this.filterConfig[filterId].defaultValue,
        },
        tags: newArr,
      },
      () => this.props.onValueChange(this.state.values)
    );
  };
}
